import { useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { Heart, UserCheck } from "lucide-react";

export type Burst = { key: number; kind: "heart" | "follow"; label?: string };

export function useTapBurst() {
  const counter = useRef(0);
  const [burst, setBurst] = useState<Burst | null>(null);
  const fire = (kind: Burst["kind"], label?: string) => {
    counter.current += 1;
    setBurst({ key: counter.current, kind, label });
  };
  return { burst, fire, clear: () => setBurst(null) };
}

// Double tap on the page surface, ignoring anything interactive so buttons and
// links keep their single-tap meaning. Click-based rather than touch-based: a
// click fires for both tap and mouse, and the WebView suppresses double-tap
// zoom, so two clicks inside the window is a reliable double tap.
export function useSurfaceDoubleTap(onDouble: () => void) {
  const lastRef = useRef(0);
  const handlerRef = useRef(onDouble);
  handlerRef.current = onDouble;
  return (event: ReactMouseEvent) => {
    const target = event.target as Element | null;
    if (target?.closest("button, a, input, select, textarea, [role=button]")) return;
    const now = performance.now();
    if (now - lastRef.current < 300) {
      lastRef.current = 0;
      handlerRef.current();
    } else {
      lastRef.current = now;
    }
  };
}

export function TapBurst({ burst, onDone }: { burst: Burst | null; onDone: () => void }) {
  if (!burst) return null;
  return (
    <div
      key={burst.key}
      className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center"
      aria-hidden="true"
    >
      <div className="stoop-tap-burst flex flex-col items-center gap-2" onAnimationEnd={onDone}>
        {burst.kind === "heart" ? (
          <Heart className="h-24 w-24 fill-red-500 text-red-500 drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]" />
        ) : (
          <UserCheck
            className="h-24 w-24 text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.55)]"
            strokeWidth={2.5}
          />
        )}
        {burst.label && (
          <span className="rounded-full bg-black/60 px-3 py-1 text-sm font-semibold text-white backdrop-blur">
            {burst.label}
          </span>
        )}
      </div>
    </div>
  );
}
